<template>
  <div class="kids-cart-page">
    <MainNavbar />
    <KidsStoreNav />

    <main class="kids-cart">
      <header class="kids-cart__header">
        <h1>
          <i class="fa-solid fa-cart-shopping" aria-hidden="true" />
          Kids Cart
        </h1>
        <p v-if="cartItems.length">
          {{ totalItems }} {{ totalItems === 1 ? "item" : "items" }}
        </p>
      </header>

      <div v-if="cartItems.length" class="kids-cart__layout">
        <section class="kids-cart__items" aria-label="Cart items">
          <article
            v-for="item in cartItems"
            :key="item.item_id"
            class="kids-cart__item"
          >
            <img
              :src="getImageUrl(item.thumbnail)"
              :alt="item.name"
              class="kids-cart__thumb"
              loading="lazy"
              @error="onImageError"
            />

            <div class="kids-cart__info">
              <h2>{{ item.name }}</h2>
              <p class="kids-cart__price">KES {{ formatPrice(item.price) }} each</p>

              <div class="kids-cart__qty">
                <button type="button" :disabled="item.quantity <= 1" @click="decreaseQty(item)">−</button>
                <span>{{ item.quantity }}</span>
                <button type="button" @click="increaseQty(item)">+</button>
              </div>

              <div v-if="itemNeedsSize(item)" class="kids-cart__sizes">
                <p class="kids-cart__sizes-title">
                  {{ sizeFieldLabel(item) }} (one per item)
                </p>
                <div
                  v-for="unitIndex in Number(item.quantity)"
                  :key="`${item.item_id}-size-${unitIndex}`"
                  class="kids-cart__size-row"
                >
                  <label :for="`kids-size-${item.item_id}-${unitIndex}`">
                    {{ isFootwearItem(item) ? `Pair ${unitIndex}` : `Item ${unitIndex}` }}
                  </label>
                  <select
                    :id="`kids-size-${item.item_id}-${unitIndex}`"
                    :value="item.sizes?.[unitIndex - 1] || ''"
                    @change="updateItemSize(item, unitIndex - 1, $event.target.value)"
                  >
                    <option value="" disabled>Select size</option>
                    <option
                      v-for="option in sizeOptionsForItem(item)"
                      :key="`${item.item_id}-${unitIndex}-${option}`"
                      :value="option"
                    >
                      {{ option }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div class="kids-cart__aside">
              <p class="kids-cart__line-total">
                KES {{ formatPrice(kidsCartStore.getItemTotal(item)) }}
              </p>
              <button type="button" class="kids-cart__remove" @click="removeItem(item)">
                <i class="fa-solid fa-trash" aria-hidden="true" />
                Remove
              </button>
            </div>
          </article>
        </section>

        <aside class="kids-cart__summary">
          <h2>Order summary</h2>
          <div class="kids-cart__summary-row">
            <span>Subtotal</span>
            <span>KES {{ formatPrice(kidsCartStore.cartTotal) }}</span>
          </div>
          <div class="kids-cart__summary-row">
            <span>Delivery</span>
            <span>{{ delivery.isWithinCBD ? "Free" : `KES ${formatPrice(deliveryFee)}` }}</span>
          </div>
          <div class="kids-cart__summary-row kids-cart__summary-row--total">
            <strong>Total</strong>
            <strong>KES {{ formatPrice(total) }}</strong>
          </div>

          <form class="kids-cart__form" @submit.prevent="showConfirmation">
            <label>
              Delivery address
              <input v-model.trim="delivery.address" type="text" required minlength="5" />
            </label>
            <label>
              City / town
              <input
                v-model.trim="delivery.city"
                type="text"
                required
                minlength="2"
                @input="checkCBDStatus"
              />
            </label>
            <label>
              Google Maps pin (optional)
              <input v-model.trim="delivery.pinLocation" type="url" placeholder="https://maps.google.com/..." />
            </label>
            <label>
              Notes (optional)
              <textarea v-model.trim="delivery.notes" rows="2" maxlength="500" />
            </label>

            <button type="submit" class="kids-cart__checkout" :disabled="isProcessing">
              {{ isProcessing ? "Processing…" : "Place order" }}
            </button>
          </form>
        </aside>
      </div>

      <div v-else class="kids-cart__empty">
        <i class="fa-solid fa-cart-shopping" aria-hidden="true" />
        <h2>Your kids cart is empty</h2>
        <p>Add gear from the kids store to get started.</p>
        <router-link :to="{ name: storeRoute }" class="kids-cart__browse">
          Browse kids store
        </router-link>
      </div>
    </main>

    <transition name="fade">
      <div v-if="showModal" class="kids-cart__modal-backdrop" @click.self="closeModal">
        <div class="kids-cart__modal" role="dialog" aria-modal="true" aria-labelledby="kids-confirm-title">
          <h2 id="kids-confirm-title">Confirm your order</h2>
          <p>Review delivery details before placing your kids store order.</p>
          <ul class="kids-cart__confirm-list">
            <li><strong>Address:</strong> {{ delivery.address }}</li>
            <li><strong>City:</strong> {{ delivery.city }}</li>
            <li><strong>Total:</strong> KES {{ formatPrice(total) }}</li>
          </ul>
          <p v-if="errorMessage" class="kids-cart__error">{{ errorMessage }}</p>
          <div class="kids-cart__modal-actions">
            <button type="button" class="kids-cart__btn-secondary" :disabled="isProcessing" @click="closeModal">
              Back
            </button>
            <button type="button" class="kids-cart__checkout" :disabled="isProcessing" @click="finalizeOrder">
              {{ isProcessing ? "Placing order…" : "Confirm order" }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <MainFooter />
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import MainFooter from "@/components/main/headernfooter/Footer.vue";
import KidsStoreNav from "./components/KidsStoreNav.vue";
import { useKidsCartStore } from "@/cartstore/kidsCartStore";
import { useAuthStore } from "@/authen/auth";
import generateHmacSignature from "@/utils/utils";
import { buildApiUrl } from "@/config/api";
import globalToastMixin from "@/mixins/globalToastMixin";
import {
  ensureItemSizeSlots,
  isFootwearItem,
  itemNeedsSize,
  itemSizesComplete,
  sizeFieldLabel,
  sizeOptionsForItem,
} from "@/utils/orderSizes";
import { KIDS_API, KIDS_STORE_ROUTE } from "./constants";

export default {
  name: "KidsCart",
  components: { MainNavbar, MainFooter, KidsStoreNav },
  mixins: [globalToastMixin],
  setup() {
    const kidsCartStore = useKidsCartStore();
    const authStore = useAuthStore();
    return { kidsCartStore, authStore };
  },
  data() {
    return {
      storeRoute: KIDS_STORE_ROUTE,
      showModal: false,
      isProcessing: false,
      errorMessage: "",
      deliveryFee: 250,
      delivery: {
        address: "",
        city: "Nairobi CBD",
        pinLocation: "",
        notes: "",
        isWithinCBD: true,
      },
      cbdAreas: ["nairobi cbd", "westlands", "kilimani", "parklands", "upper hill", "kileleshwa"],
    };
  },
  computed: {
    cartItems() {
      return this.kidsCartStore.items;
    },
    totalItems() {
      return this.kidsCartStore.cartCount;
    },
    total() {
      return this.kidsCartStore.cartTotal + (this.delivery.isWithinCBD ? 0 : this.deliveryFee);
    },
  },
  created() {
    this.kidsCartStore.syncAllItemSizeSlots();
    this.kidsCartStore.saveCart();
  },
  methods: {
    itemNeedsSize,
    isFootwearItem,
    sizeOptionsForItem,
    sizeFieldLabel,
    formatPrice(value) {
      return Number(value || 0).toLocaleString("en-KE");
    },
    getImageUrl(filename) {
      if (!filename) return require("@/assets/ipalogo.png");
      if (String(filename).startsWith("http")) return filename;
      return filename;
    },
    onImageError(event) {
      event.target.src = require("@/assets/ipalogo.png");
      event.target.onerror = null;
    },
    checkCBDStatus() {
      const city = this.delivery.city.toLowerCase().trim();
      this.delivery.isWithinCBD = this.cbdAreas.some((area) => city.includes(area));
    },
    increaseQty(item) {
      this.kidsCartStore.increaseQty(item);
    },
    decreaseQty(item) {
      this.kidsCartStore.decreaseQty(item);
    },
    removeItem(item) {
      this.kidsCartStore.removeFromCart(item.item_id);
    },
    updateItemSize(item, unitIndex, value) {
      this.kidsCartStore.setItemSize(item.item_id, unitIndex, value);
    },
    validateForm() {
      if (!this.authStore.isAuthenticated || !this.authStore.user) {
        this.showToast("Authentication required", "Please log in to checkout.", "warning");
        this.$router.push("/ipa/sign-in");
        return false;
      }
      if (!this.delivery.address.trim() || this.delivery.address.trim().length < 5) {
        this.showToast("Missing information", "Please enter your delivery address.", "warning");
        return false;
      }
      if (!this.delivery.city.trim()) {
        this.showToast("Missing information", "Please enter your city.", "warning");
        return false;
      }
      this.kidsCartStore.syncAllItemSizeSlots();
      const incomplete = this.cartItems.find((item) => !itemSizesComplete(item));
      if (incomplete) {
        this.showToast(
          "Select sizes",
          `Choose ${sizeFieldLabel(incomplete).toLowerCase()} for every unit of "${incomplete.name}".`,
          "warning",
        );
        return false;
      }
      return true;
    },
    showConfirmation() {
      if (!this.validateForm()) return;
      this.errorMessage = "";
      this.showModal = true;
    },
    closeModal() {
      if (this.isProcessing) return;
      this.showModal = false;
    },
    async finalizeOrder() {
      if (!this.validateForm()) return;
      this.isProcessing = true;
      this.errorMessage = "";

      try {
        const token = localStorage.getItem("access_token");
        const user = this.authStore.user;
        const customerName = user?.full_name || user?.name || user?.customer_name || "Customer";

        const orderPayload = {
          customer_name: customerName.trim(),
          customer_phone: user?.phone || user?.phone_number || "",
          delivery_address: this.delivery.address.trim(),
          delivery_city: this.delivery.city.trim(),
          pin_location: this.delivery.pinLocation?.trim() || null,
          delivery_notes: this.delivery.notes?.trim() || null,
          delivery_fee: parseFloat((this.delivery.isWithinCBD ? 0 : this.deliveryFee).toFixed(2)),
          items: this.cartItems.map((item) => {
            ensureItemSizeSlots(item);
            const payloadItem = {
              item_id: String(item.item_id),
              name: String(item.name).trim(),
              quantity: parseInt(item.quantity, 10),
              price: parseFloat(Number(item.price).toFixed(2)),
            };
            if (item.sub_category) {
              payloadItem.sub_category = String(item.sub_category).trim().toLowerCase();
            }
            if (itemNeedsSize(item) && Array.isArray(item.sizes) && item.sizes.length) {
              payloadItem.sizes = item.sizes.map((value) => String(value).trim());
            }
            return payloadItem;
          }),
          subtotal: parseFloat(Number(this.kidsCartStore.cartTotal).toFixed(2)),
          total_amount: parseFloat(Number(this.total).toFixed(2)),
          payment_method: "cash_on_delivery",
          currency: "KES",
          status: "pending",
        };

        const hmacPayload = generateHmacSignature(orderPayload);
        const response = await fetch(buildApiUrl(KIDS_API.orderCreation), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(hmacPayload),
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.detail || data.message || "Could not place order.");
        }

        this.kidsCartStore.clearCart();
        this.showModal = false;
        this.showToast(
          "Order placed",
          `Kids order #${data.order_number} created successfully.`,
          "success",
        );
        this.$router.push({ name: KIDS_STORE_ROUTE });
      } catch (err) {
        this.errorMessage = err.message || "Could not place order.";
        this.showToast("Order failed", this.errorMessage, "error");
      } finally {
        this.isProcessing = false;
      }
    },
  },
};
</script>

<style scoped>
.kids-cart-page {
  min-height: 100vh;
  min-height: 100dvh;
  background: var(--background-color2);
  color: var(--text-color1);
}

.kids-cart {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem clamp(0.875rem, 3.5vw, 2.75rem) 3rem;
  padding-top: max(68px, calc(env(safe-area-inset-top, 0px) + 60px));
  box-sizing: border-box;
}

.kids-cart__header h1 {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.25rem;
  font-size: clamp(1.25rem, 3vw, 1.75rem);
}

.kids-cart__header p {
  margin: 0 0 1.25rem;
  color: var(--text-color2);
}

.kids-cart__layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 340px);
  gap: 1.25rem;
  align-items: start;
}

.kids-cart__item {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr) auto;
  gap: 0.85rem;
  padding: 1rem;
  margin-bottom: 0.75rem;
  border-radius: 14px;
  background: var(--background-color1);
  border: 1px solid color-mix(in srgb, var(--border-color) 80%, transparent);
}

.kids-cart__thumb {
  width: 88px;
  height: 88px;
  object-fit: cover;
  border-radius: 10px;
  background: var(--background-color2);
}

.kids-cart__info h2 {
  margin: 0 0 0.35rem;
  font-size: 1rem;
}

.kids-cart__price {
  margin: 0 0 0.65rem;
  color: var(--text-color2);
  font-size: 0.88rem;
}

.kids-cart__qty {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.65rem;
}

.kids-cart__qty button {
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--background-color2);
  color: var(--text-color1);
  cursor: pointer;
}

.kids-cart__sizes-title {
  margin: 0 0 0.35rem;
  font-size: 0.82rem;
  font-weight: 600;
}

.kids-cart__size-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
  font-size: 0.82rem;
}

.kids-cart__size-row select {
  flex: 1;
  min-width: 0;
  padding: 0.35rem 0.5rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: var(--background-color2);
  color: var(--text-color1);
}

.kids-cart__aside {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
}

.kids-cart__line-total {
  margin: 0;
  font-weight: 700;
}

.kids-cart__remove {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: none;
  background: transparent;
  color: #c0392b;
  cursor: pointer;
  font-size: 0.82rem;
}

.kids-cart__summary {
  position: sticky;
  top: calc(max(60px, env(safe-area-inset-top, 0px) + 56px) + 52px);
  padding: 1rem;
  border-radius: 14px;
  background: var(--background-color1);
  border: 1px solid color-mix(in srgb, var(--border-color) 80%, transparent);
}

.kids-cart__summary h2 {
  margin: 0 0 0.75rem;
  font-size: 1.05rem;
}

.kids-cart__summary-row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.45rem;
  font-size: 0.9rem;
}

.kids-cart__summary-row--total {
  margin-top: 0.65rem;
  padding-top: 0.65rem;
  border-top: 1px solid var(--border-color);
}

.kids-cart__form {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin-top: 1rem;
}

.kids-cart__form label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.82rem;
  font-weight: 600;
}

.kids-cart__form input,
.kids-cart__form textarea {
  padding: 0.55rem 0.65rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: var(--background-color2);
  color: var(--text-color1);
  font: inherit;
}

.kids-cart__checkout {
  margin-top: 0.35rem;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 10px;
  background: var(--border-color-hover);
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}

.kids-cart__checkout:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.kids-cart__empty {
  text-align: center;
  padding: 3rem 1rem;
}

.kids-cart__empty i {
  font-size: 2.5rem;
  color: var(--text-color2);
}

.kids-cart__browse {
  display: inline-flex;
  margin-top: 1rem;
  padding: 0.65rem 1rem;
  border-radius: 999px;
  background: var(--border-color-hover);
  color: #fff;
  text-decoration: none;
  font-weight: 600;
}

.kids-cart__modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.45);
}

.kids-cart__modal {
  width: min(100%, 420px);
  padding: 1.25rem;
  border-radius: 14px;
  background: var(--background-color1);
}

.kids-cart__confirm-list {
  margin: 0.75rem 0;
  padding-left: 1.1rem;
  font-size: 0.9rem;
}

.kids-cart__error {
  color: #c0392b;
  font-size: 0.88rem;
}

.kids-cart__modal-actions {
  display: flex;
  gap: 0.65rem;
  justify-content: flex-end;
}

.kids-cart__btn-secondary {
  padding: 0.65rem 0.9rem;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  background: transparent;
  color: var(--text-color1);
  cursor: pointer;
}

@media (max-width: 860px) {
  .kids-cart__layout {
    grid-template-columns: 1fr;
  }

  .kids-cart__summary {
    position: static;
  }

  .kids-cart__item {
    grid-template-columns: 72px minmax(0, 1fr);
  }

  .kids-cart__aside {
    grid-column: 1 / -1;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}
</style>
